import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import {
  SettersMap,
  SetterConfigGetter,
  SetterData,
  WholeProperty,
  SubProperty,
  SetterConfigValueMap,
} from '../types';
import { CssPropertyEnumCssPropertyType } from '@wix/ambassador-devcenter-components-v1-component/types';
import {
  getParentShorthands,
  isShorthand,
  getShorthandExpansions,
} from '../propertyRelationships';
import { getEquivalentSiblings } from '../propertyEquivalents';

const createWholeProperty = (): WholeProperty => ({
  type: 'whole',
});

const createSubProperty = (subKey: string): SubProperty => ({
  type: 'sub',
  subKey,
});

const createWholeTarget = (
  setterData: SetterData,
): WholeProperty & SetterData => ({
  type: 'whole',
  styleItemId: setterData.styleItemId,
  editorPointer: setterData.editorPointer,
  context: setterData.context,
  cssPropertyType: setterData.cssPropertyType,
  styleNamespace: setterData.styleNamespace,
  engine: setterData.engine,
});

const createSubTarget = (
  subKey: string,
  setterData: SetterData,
): SubProperty & SetterData => ({
  type: 'sub',
  subKey,
  styleItemId: setterData.styleItemId,
  editorPointer: setterData.editorPointer,
  context: setterData.context,
  cssPropertyType: setterData.cssPropertyType,
  styleNamespace: setterData.styleNamespace,
  engine: setterData.engine,
});

const createSelfSetter =
  (): SetterConfigGetter => (setterData: SetterData) => ({
    source: createWholeProperty(),
    target: createWholeTarget(setterData),
  });

const createShorthandToSubSetter =
  (subKey: string): SetterConfigGetter =>
  (setterData: SetterData) => ({
    source: createWholeProperty(),
    target: createSubTarget(subKey, setterData),
  });

const createParentToSubSetter =
  (subKey: string): SetterConfigGetter =>
  (setterData: SetterData) => ({
    source: createSubProperty(subKey),
    target: createSubTarget(subKey, setterData),
  });

const createSubToShorthandSetter =
  (subKey: string): SetterConfigGetter =>
  (setterData: SetterData) => ({
    source: createSubProperty(subKey),
    target: createWholeTarget(setterData),
  });

const addSetterToMap = (
  map: SetterConfigValueMap,
  key: CssPropertyEnumCssPropertyType,
  setter: SetterConfigGetter,
) => {
  if (!map[key]) {
    map[key] = [];
  }
  map[key]!.push(setter);
};

const addAllParentProperties = (
  setters: SetterConfigValueMap,
  cssProperty: string,
) => {
  const parents = getParentShorthands(cssProperty);
  parents.forEach((shorthand) => {
    addSetterToMap(
      setters,
      shorthand as CssPropertyEnumCssPropertyType,
      createSubToShorthandSetter(cssProperty),
    );
  });
};

const addEquivalentProperties = (
  setters: SetterConfigValueMap,
  cssProperty: string,
) => {
  getEquivalentSiblings(cssProperty).forEach((equivalentProperty) => {
    if (equivalentProperty !== cssProperty) {
      addSetterToMap(
        setters,
        equivalentProperty as CssPropertyEnumCssPropertyType,
        createSelfSetter(),
      );
    }
  });
};

export const deriveSettersConfig = (): SettersMap => {
  const settersMap = {} as SettersMap;

  Object.values(CSS_PROPERTIES.CSS_PROPERTY_TYPE).forEach((cssProperty) => {
    const standardSetters: SetterConfigValueMap = {};
    const siblingSetters: SetterConfigValueMap = {};

    // Self-setter
    addSetterToMap(
      standardSetters,
      cssProperty as CssPropertyEnumCssPropertyType,
      createSelfSetter(),
    );

    // Shorthand relationships
    if (isShorthand(cssProperty)) {
      const expansions = getShorthandExpansions(cssProperty);
      expansions.forEach((expansion) => {
        addSetterToMap(
          standardSetters,
          expansion.propertyName as CssPropertyEnumCssPropertyType,
          createShorthandToSubSetter(expansion.propertyName),
        );

        const atomicParents = getParentShorthands(expansion.propertyName);

        if (atomicParents.length > 1) {
          atomicParents.forEach((parent) => {
            if (parent !== cssProperty) {
              addSetterToMap(
                standardSetters,
                parent as CssPropertyEnumCssPropertyType,
                createParentToSubSetter(expansion.propertyName),
              );
            }
          });
        }
      });
    }

    // Parent properties (shorthand composition)
    addAllParentProperties(standardSetters, cssProperty);

    // Value-equivalent properties (for ValueSet mode propagation)
    // First, add direct equivalents of the cssProperty itself
    addEquivalentProperties(siblingSetters, cssProperty);

    // Then, for ALL properties that now have setters in standardSetters,
    // add value-equivalent cross-references to enable full cross-property propagation in ValueSet mode
    // This handles cases like borderWidth → borderInlineStart where inline properties
    // are not in the physical shorthand's decomposition but should still receive updates
    Object.keys(standardSetters).forEach((targetProperty) => {
      getEquivalentSiblings(targetProperty).forEach((equivalentProperty) => {
        if (equivalentProperty !== targetProperty) {
          // Copy all setters from targetProperty to its equivalents
          const settersForTarget =
            standardSetters[targetProperty as CssPropertyEnumCssPropertyType];

          settersForTarget?.forEach((setterGetter: SetterConfigGetter) => {
            addSetterToMap(
              siblingSetters,
              equivalentProperty as CssPropertyEnumCssPropertyType,
              setterGetter,
            );
          });
        }
      });
    });

    settersMap[cssProperty] = {
      standard: standardSetters,
      equivalents: siblingSetters,
    };
  });

  return settersMap;
};

let cachedSettersMap: SettersMap | undefined;

export const getSettersMap = (): SettersMap => {
  if (!cachedSettersMap) {
    cachedSettersMap = deriveSettersConfig();
  }
  return cachedSettersMap;
};
